/**
 * @fileoverview Per-section policy consent gate with content hashing.
 *
 * Each policy section (e.g. risks, disclaimer, terms, privacy) is
 * tracked independently as `{ hash, acceptedAt }`. The hash is a cheap
 * FNV-1a digest of the section content — when an author edits a
 * section, the stored hash mismatches and only that section re-prompts;
 * unchanged sections stay accepted. This avoids the "edit a typo,
 * every user is re-prompted on every section" papercut of single-boolean
 * gates and produces an audit trail for free.
 *
 * Pure utilities only — no React, no DOM, no project-specific section
 * keys. The UI binds these primitives to its own section definitions
 * and storage-key string.
 *
 * Ported from the ProveX monorepo (@provex/utils). See
 * `~/.claude/skills/per-section-policy-consent-hashing/SKILL.md`
 * for the broader design rationale, layout candidates, and trade-offs.
 */

/** A single per-section consent receipt. */
export interface ConsentEntry {
  /** Content hash at the time of acceptance — see {@link hashString}. */
  hash: string
  /** ISO-8601 timestamp the user accepted at. */
  acceptedAt: string
}

/** Map of section key (caller-defined) to its consent receipt. */
export type ConsentState = Record<string, ConsentEntry | undefined>

/** Three-valued status of a single section. */
export type SectionStatusValue = 'unread' | 'accepted' | 'stale'

/**
 * Minimal structural shape used by this module. Satisfied by
 * `globalThis.localStorage` in browsers and by any in-memory shim in
 * tests — the module never depends on a real DOM Storage instance.
 */
export interface ConsentStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * Returns the ambient browser storage, or `null` in non-browser /
 * private-mode / SSR contexts. Never throws — failure to access storage
 * is normal, not exceptional.
 */
function ambientStorage(): ConsentStorage | null {
  try {
    if (typeof globalThis === 'undefined') return null
    const candidate = (globalThis as { localStorage?: unknown }).localStorage
    if (
      candidate !== null &&
      typeof candidate === 'object' &&
      'getItem' in candidate &&
      'setItem' in candidate &&
      'removeItem' in candidate
    ) {
      return candidate as ConsentStorage
    }
    return null
  } catch {
    return null
  }
}

/**
 * FNV-1a 32-bit hash, hex-truncated to 8 characters. Sync, no
 * dependencies, non-cryptographic — the threat model is "did our
 * authors edit this string", not adversarial.
 *
 * If a compliance requirement forces SHA-256 via SubtleCrypto, bump
 * the storage key version (e.g. `sacrifice_consent_v2`) and migrate.
 */
export function hashString(s: string): string {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ('00000000' + (h >>> 0).toString(16)).slice(-8)
}

/**
 * Compact relative-time formatter for the audit row next to the accept
 * button. Past offsets only — future timestamps clamp to "just now".
 * Falls back to a locale date string once the offset exceeds a week
 * because "12d ago" is less useful than the actual date by then.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const sec = Math.max(0, Math.floor((now.getTime() - then) / 1000))
  if (sec < 5) return 'just now'
  if (sec < 60) return sec + 's ago'
  const min = Math.floor(sec / 60)
  if (min < 60) return min + 'm ago'
  const hr = Math.floor(min / 60)
  if (hr < 24) return hr + 'h ago'
  const day = Math.floor(hr / 24)
  if (day < 7) return day + 'd ago'
  return new Date(iso).toLocaleDateString()
}

/**
 * Reads the persisted consent state for a given storage key.
 * Always returns a usable object — malformed JSON, missing keys, or a
 * value of the wrong shape all degrade to `{}` rather than throwing,
 * because surfacing a parse error at modal open is worse than
 * re-prompting the user.
 */
export function readConsent(
  storageKey: string,
  storage: ConsentStorage | null = ambientStorage(),
): ConsentState {
  if (!storage) return {}
  try {
    const raw = storage.getItem(storageKey)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {}
    }
    return parsed as ConsentState
  } catch {
    return {}
  }
}

/**
 * Writes the consent state. Silently no-ops when storage is unavailable
 * or write fails — private mode and quota errors should not break the
 * accept flow, just fail to persist across sessions.
 */
export function writeConsent(
  storageKey: string,
  state: ConsentState,
  storage: ConsentStorage | null = ambientStorage(),
): void {
  if (!storage) return
  try {
    storage.setItem(storageKey, JSON.stringify(state))
  } catch {
    // Quota / private-mode — accept stands for the session but won't survive a reload.
  }
}

/**
 * Computes the three-valued status of a single section from its stored
 * entry and the current content hash. Pure — caller owns I/O.
 */
export function sectionStatus(
  stored: ConsentEntry | undefined,
  currentHash: string,
): SectionStatusValue {
  if (!stored) return 'unread'
  return stored.hash === currentHash ? 'accepted' : 'stale'
}

export interface AcceptSectionOptions {
  storage?: ConsentStorage | null
  now?: Date
}

/**
 * Persists acceptance of a single section, preserving any sibling
 * sections that already have entries. Returns the new state so React
 * callers can update local state without re-reading storage.
 */
export function acceptSection(
  storageKey: string,
  sectionKey: string,
  currentHash: string,
  opts: AcceptSectionOptions = {},
): ConsentState {
  const storage =
    opts.storage === undefined ? ambientStorage() : opts.storage
  const now = opts.now ?? new Date()
  const previous = readConsent(storageKey, storage)
  const next: ConsentState = {
    ...previous,
    [sectionKey]: { hash: currentHash, acceptedAt: now.toISOString() },
  }
  writeConsent(storageKey, next, storage)
  return next
}

/**
 * The gate: returns true only when every section listed in
 * `currentHashes` has a stored entry whose hash matches. Empty
 * `currentHashes` returns false so the gate stays closed by default.
 */
export function allAccepted(
  storageKey: string,
  currentHashes: Record<string, string>,
  storage: ConsentStorage | null = ambientStorage(),
): boolean {
  const keys = Object.keys(currentHashes)
  if (keys.length === 0) return false
  const state = readConsent(storageKey, storage)
  return keys.every(
    (k) => sectionStatus(state[k], currentHashes[k] as string) === 'accepted',
  )
}

export interface MigrateLegacyBooleanOptions {
  legacyKey: string
  storageKey: string
  currentHashes: Record<string, string>
  storage?: ConsentStorage | null
  now?: Date
}

/**
 * One-time migration from a single legacy boolean ("user accepted the
 * old monolithic risk modal") to the per-section state shape. Treats
 * the legacy flag as "all sections accepted at migration time" so
 * existing users don't see the modal again on first open.
 *
 * Returns true iff migration ran. No-op (returns false) when:
 *   - The legacy key is unset or not exactly `'1'`.
 *   - Storage isn't available.
 *   - Per-section state already exists for the new key — the new state
 *     wins; the legacy boolean is left intact in case a downstream
 *     migration wants to inspect it.
 */
export function migrateLegacyBoolean(
  opts: MigrateLegacyBooleanOptions,
): boolean {
  const storage =
    opts.storage === undefined ? ambientStorage() : opts.storage
  if (!storage) return false
  if (storage.getItem(opts.legacyKey) !== '1') return false
  const existing = readConsent(opts.storageKey, storage)
  if (Object.keys(existing).length > 0) return false
  const acceptedAt = (opts.now ?? new Date()).toISOString()
  const state: ConsentState = {}
  for (const k of Object.keys(opts.currentHashes)) {
    state[k] = { hash: opts.currentHashes[k] as string, acceptedAt }
  }
  writeConsent(opts.storageKey, state, storage)
  storage.removeItem(opts.legacyKey)
  return true
}
